import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { X } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { closeSignInPrompt } from "../../redux/features/authPrompt/authPromptSlice";
import { useEffect, useCallback } from "react";

const messages = defineMessages({
  title: {
    id: "auth.signInPrompt.title",
    defaultMessage: "Sign in to save jobs",
    description: "Title for sign-in prompt modal encouraging users to sign in to save jobs",
  },
  description: {
    id: "auth.signInPrompt.description",
    defaultMessage: "Create a free account to save jobs, sync progress, and pick up your search on any device.",
    description: "Description text in sign-in prompt modal explaining benefits of creating an account",
  },
  primaryAction: {
    id: "auth.signInPrompt.primaryAction",
    defaultMessage: "Sign in now",
    description: "Primary button label in sign-in prompt modal to sign in immediately",
  },
  secondaryAction: {
    id: "auth.signInPrompt.secondaryAction",
    defaultMessage: "Maybe later",
    description: "Secondary button label in sign-in prompt modal to dismiss the prompt",
  },
  closeLabel: {
    id: "auth.signInPrompt.closeLabel",
    defaultMessage: "Close",
    description: "Accessibility label for close button in sign-in prompt modal",
  },
});

export function SignInPromptModal() {
  const intl = useIntl();
  const { isAuthenticated, signIn } = useAuth();
  const isOpen = useAppSelector((state) => state.authPrompt.isSignInPromptOpen);
  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => {
    dispatch(closeSignInPrompt());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && isOpen) {
      dispatch(closeSignInPrompt());
    }
  }, [dispatch, isAuthenticated, isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sign-in-prompt-title"
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 px-4 py-8"
    >
      <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl dark:bg-gray-900">
        <button
          type="button"
          onClick={handleClose}
          aria-label={intl.formatMessage(messages.closeLabel)}
          className="absolute right-4 top-4 rounded-full p-2 text-gray-500 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <span className="sr-only">
            <FormattedMessage {...messages.closeLabel} />
          </span>
          <X className="h-5 w-5" />
        </button>
        <div className="flex flex-col gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-300">
            <svg
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.5 18C6.5 15.5147 8.51472 13.5 11 13.5H13C15.4853 13.5 17.5 15.5147 17.5 18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 6L6 4M20 6L18 4M4 14L6 16M18 16L20 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 id="sign-in-prompt-title" className="text-2xl font-semibold text-gray-900 dark:text-white">
              <FormattedMessage {...messages.title} />
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-300">
              <FormattedMessage {...messages.description} />
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-11 items-center justify-center rounded-full border border-gray-200 px-4 text-sm font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <FormattedMessage {...messages.secondaryAction} />
            </button>
            <button
              type="button"
              onClick={() => {
                handleClose();
                signIn();
              }}
              className="inline-flex h-11 items-center justify-center rounded-full bg-green-500 px-5 text-sm font-semibold text-white shadow-lg shadow-green-500/30 transition hover:bg-green-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <FormattedMessage {...messages.primaryAction} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


